import React from 'react';
import { cn } from '@utils/cn';

type ProgressSize = 'sm' | 'md' | 'lg';
type ProgressVariant =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'error';

interface ProgressProps {
  value: number;
  size?: ProgressSize;
  variant?: ProgressVariant;
  showPercentage?: boolean;
  label?: string;
  className?: string;
  animated?: boolean;
}

export const Progress: React.FC<ProgressProps> = ({
  value,
  size = 'md',
  variant = 'primary',
  showPercentage = false,
  label,
  className,
  animated = true,
}) => {
  // Clamp value between 0 and 100
  const clampedValue = Math.min(Math.max(value, 0), 100);

  const sizeClasses: Record<ProgressSize, string> = {
    sm: 'h-2',
    md: 'h-3',
    lg: 'h-4',
  };

  const variantClasses: Record<ProgressVariant, string> = {
    primary: 'bg-indigo-900 dark:bg-indigo-700',
    secondary: 'bg-gray-500 dark:bg-gray-400',
    success: 'bg-green-600 dark:bg-green-500',
    warning: 'bg-yellow-500 dark:bg-yellow-400',
    error: 'bg-red-600 dark:bg-red-500',
  };

  const textSizeClasses: Record<ProgressSize, string> = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
  };

  return (
    <div className={cn('w-full', className)}>
      {(showPercentage || label) && (
        <div className="flex justify-between items-center mb-1">
          <span
            className={cn(
              'font-medium text-gray-700 dark:text-gray-300',
              textSizeClasses[size],
            )}>
            {label || `${Math.round(clampedValue)}%`}
          </span>
        </div>
      )}
      <div
        className={cn(
          'w-full bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden',
          sizeClasses[size],
        )}>
        <div
          className={cn(
            'h-full rounded-full transition-all duration-300 ease-out',
            variantClasses[variant],
            animated && 'transition-all duration-500 ease-out',
          )}
          style={{ width: `${clampedValue}%` }}
        />
      </div>
    </div>
  );
};
